import type { CoreStoreType } from '../../store/CoreStore';
import { createGameStore } from '../../store/CoreStore';
import { chatActions } from '../../store/slices/chat/slice';
import type { Message, UserType } from '../../store/slices/chat/types';
import { ChatBlockType } from '../../store/slices/chat/types';
import { subtitlesActions } from '../../store/slices/subtitles/slice';
import { createChatAvailableMessage, isBetRequiredToChat, parseChatMessage } from '../../utils/chat';
import { RestartController } from '../restartController/restartController';

import type { IChatCommunicationProcessor } from './types';

type StoreAction =
  | ReturnType<typeof chatActions.storeGameChatMessage>
  | ReturnType<typeof chatActions.storeLiveSupportMessage>
  | ReturnType<typeof chatActions.storeLastChatMessage>
  | ReturnType<typeof chatActions.setLiveSupportReadStatus>
  | ReturnType<typeof chatActions.setIsBetChatAllowed>
  | ReturnType<typeof chatActions.setChatBlockType>
  | ReturnType<typeof chatActions.setIsGameChatBlocked>
  | ReturnType<typeof chatActions.setIsLiveSupportChatBlocked>
  | ReturnType<typeof chatActions.clearLiveSupportMessages>
  | ReturnType<typeof chatActions.setChatSubscriptionState>
  | ReturnType<typeof chatActions.removeGameChatMessage>
  | ReturnType<typeof subtitlesActions.setSubtitle>;

export class ChatCommunicationController {
  #ws?: WebSocket;
  #pingInterval?: number;
  #store?: CoreStoreType;
  #processors: Record<string, IChatCommunicationProcessor> = {};

  private url: string | null = null;
  private jSessionId: string | null = null;
  private userId: string | null = null;
  private language: string | null = null;
  private subLanguage: string | null = null;
  private tableId: string | null = null;
  private casinoId: string | null = null;
  private tableName: string | null = null;
  private messageId: string | null = null;
  private showOtherMessages: boolean = true;
  private subtitlesEnabled: boolean = false;
  private chatBlockType: ChatBlockType = ChatBlockType.LowBalance;
  private isGamePaused = false;

  constructor() {
    this.onWebSocketMessage = this.onWebSocketMessage.bind(this);
    this.handleWebSocketError = this.handleWebSocketError.bind(this);
    this.handleWebSocketClose = this.handleWebSocketClose.bind(this);

    RestartController.addListener('chat-communication', () => {
      this.restart();
    });
  }

  connect(
    url: string,
    jSessionId: string,
    userId: string,
    tableId: string,
    casinoId: string,
    tableName: string,
    messageId: string,
    showOtherMessages: boolean,
    subtitlesEnabled: boolean,
    language: string,
    subLang: string
  ): void {
    this.close();
    this.#ws = new WebSocket(url);
    this.#ws.addEventListener('message', this.onWebSocketMessage);
    this.#ws.addEventListener('error', this.handleWebSocketError);
    this.#ws.addEventListener('close', this.handleWebSocketClose);

    this.url = url;
    this.jSessionId = jSessionId;
    this.userId = userId;
    this.tableId = tableId;
    this.casinoId = casinoId;
    this.tableName = tableName;
    this.messageId = messageId;
    this.showOtherMessages = showOtherMessages;
    this.subtitlesEnabled = subtitlesEnabled;
    this.language = language;
    this.subLanguage = subLang;

    this.#pingInterval = window.setInterval(() => {
      if (!this.#ws) {
        return;
      }
      switch (this.#ws.readyState) {
        case WebSocket.OPEN: {
          const pingMessage = {
            action: 'ping',
            userId: userId,
            time: Date.now(),
            role: 'GAME_USER',
          };

          this.#ws.send(JSON.stringify(pingMessage));
          break;
        }
        case WebSocket.CLOSING:
        case WebSocket.CLOSED:
          this.restart();
          break;
      }
    }, 10_000);

    const subscribeMessage = {
      content: 'SUBSCRIBE',
      action: 'SUBSCRIBE',
      jSessionId,
      userId,
      tableId,
      casinoId,
      tableName,
      lang: language,
      role: 'GAME_USER',
      messageId,
      preference: {
        showOtherMessages: showOtherMessages,
        subEnabled: subtitlesEnabled,
        subLang: subLang,
      },
    };

    // Subscribe to messages from chat server
    const onOpen = (): void => {
      this.#ws?.send(JSON.stringify(subscribeMessage));
      this.#ws?.removeEventListener('open', onOpen);
    };
    this.#ws.addEventListener('open', onOpen);
  }

  reconnect(): void {
    if (
      this.url &&
      this.jSessionId &&
      this.userId &&
      this.tableId &&
      this.casinoId &&
      this.tableName &&
      this.messageId &&
      this.language &&
      this.subLanguage
    ) {
      this.connect(
        this.url,
        this.jSessionId,
        this.userId,
        this.tableId,
        this.casinoId,
        this.tableName,
        this.messageId,
        this.showOtherMessages,
        this.subtitlesEnabled,
        this.language,
        this.subLanguage
      );
    }
  }

  restart(): void {
    this.close();
    this.reconnect();
  }

  sendMessage(message: string): void {
    if (this.#ws && this.#ws.readyState === WebSocket.OPEN) {
      this.#ws.send(message);
    } else {
      console.error('WebSocket connection is not open. Message not sent.');
    }
  }

  onWebSocketMessageSuccessHandler(message: Message): void {
    // Messages filtered by support team
    switch (message.tagMessage) {
      case 'GAME_USER':
        this.dispatchToStore(chatActions.storeGameChatMessage(message));
        break;
      case 'SUPPORT':
      case 'LIVE_SUPPORT':
        this.dispatchToStore(chatActions.storeLiveSupportMessage(message));
        this.dispatchToStore(chatActions.setLiveSupportReadStatus(message.role === 'GAME_USER'));
        break;
    }
    this.processChatMessageByAction(message);
  }

  onWebSocketMessage(msg: MessageEvent): void {
    const message = parseChatMessage(JSON.parse(msg.data));

    if (message.action) {
      if (message.action === 'SHOW_HIDE_MESSAGE') {
        this.dispatchToStore(chatActions.removeGameChatMessage(message.messageId));
        return;
      }
      if (message.status === 'SUCCESS') {
        this.onWebSocketMessageSuccessHandler(message);
      }
    }
  }

  processChatMessageByAction(message: Message): void {
    switch (message.action) {
      case 'FRESHCHAT_USER_CREATION':
        this.dispatchToStore(chatActions.setChatSubscriptionState('FRESH_CHAT_SUBSCRIBED'));
        break;

      case 'COMMAND':
        this.dispatchToStore(chatActions.storeLastChatMessage(message));
        break;

      // TODO: document Freshchat subscription mechanism in Storybook, UIC-304
      case 'SUBSCRIBE':
        this.dispatchToStore(chatActions.setChatSubscriptionState('SUBSCRIBED'));
        if (message.user) {
          this.setIsBetChatAllowed(message.user.betChatAllowed, message.user.chatAllowed);
          this.setChatBlocked(message.user);
          this.setChatBlockType(message.user.chatBlockType);
        }
        break;

      case 'BLOCK':
      case 'UNBLOCK':
        this.setChatBlocked(message.user);
        break;

      case 'SUBTITLES':
        this.dispatchToStore(subtitlesActions.setSubtitle({ ...message, isFinal: true }));
        break;

      case 'CHAT_STATUS':
        this.setIsBetChatAllowed(message.betChatAllowed, message.chatAllowed, true);
        break;
    }
  }

  setChatBlockType(chatBlockType?: ChatBlockType): void {
    if (chatBlockType) {
      this.chatBlockType = chatBlockType;
      this.dispatchToStore(chatActions.setChatBlockType(chatBlockType));
    }
  }

  setIsBetChatAllowed(betChatAllowed?: boolean, chatAllowed?: boolean, isStatusChanged = false): void {
    betChatAllowed = betChatAllowed !== false;
    this.dispatchToStore(chatActions.setIsBetChatAllowed(betChatAllowed));

    if (chatAllowed && isStatusChanged && isBetRequiredToChat(this.chatBlockType)) {
      const msg = createChatAvailableMessage(betChatAllowed);
      this.dispatchToStore(chatActions.storeGameChatMessage(msg));
      this.dispatchToStore(chatActions.storeLastChatMessage(msg));
    }
  }

  setChatBlocked(user?: UserType): void {
    if (user) {
      this.dispatchToStore(chatActions.setIsGameChatBlocked(!user.chatAllowed));
      this.dispatchToStore(chatActions.setIsLiveSupportChatBlocked(!user.supportChatAllowed));
    }
  }

  setStore(store: ReturnType<typeof createGameStore>): void {
    this.#store = store;
  }

  setProcessor(processor: IChatCommunicationProcessor): void {
    this.#processors[processor.messageName] = processor;
  }

  close(): void {
    clearInterval(this.#pingInterval);

    if (this.#ws) {
      this.#ws.removeEventListener('error', this.handleWebSocketError);
      this.#ws.removeEventListener('close', this.handleWebSocketClose);
      this.#ws.removeEventListener('message', this.onWebSocketMessage);
      this.#ws.close();
      this.#ws = undefined;
    }
  }

  public updateConnectionOnGamePause(isGamePaused: boolean): void {
    if (this.isGamePaused !== isGamePaused) {
      this.isGamePaused = isGamePaused;

      if (isGamePaused) {
        this.close();
      } else {
        this.reconnect();
      }
    }
  }

  dispatchToStore(action: StoreAction): void {
    if (this.#store) {
      this.#store.dispatch(action);
    }
  }

  handleWebSocketError(e: Event): void {
    console.error('WebSocket error:', e);
  }

  handleWebSocketClose(e: CloseEvent): void {
    console.log('WebSocket closed:', e);
  }
}
